import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'
import { toast } from 'react-toastify'
import { propOr } from 'ramda'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import { CADENCE_PRESETS, CRITICALITY_LEVELS, PENALTY_SEVERITIES } from 'features/compliance/obligationConstants'
import {
  fetchAvailableObligations,
  addProgrammeObligation,
  fetchTaskDomains,
  createTaskDomain,
} from 'services/ProgrammeService'
import { getFileTypes } from 'services/BpService'
import SearchableSelect from 'components/SearchableSelect'

const KINDS = [
  { value: 'task', label: 'Task' },
  { value: 'file_type', label: 'Document' },
]

const CADENCE_BASES = [
  { value: 'statutory', label: 'Statutory' },
  { value: 'guidance', label: 'Guidance' },
  { value: 'risk_assessment', label: 'Risk-based' },
]

const DOMAIN_COLOURS = ['#c2410c', '#d97706', '#15803d', '#1f8c97', '#7C5FE0', '#b00020', '#334155']

const cadenceText = days => {
  if (days === null || days === undefined) return 'One-off'
  const match = CADENCE_PRESETS.find(p => p.days === days)
  return match ? match.label : `Every ${days} days`
}

const numOrNull = v => (v === '' || v === null || v === undefined ? null : Number(v))

const AddProgrammeObligationModal = ({ bpId, defaultDomainId = null, onClose, onAdded }) => {
  const [tab, setTab] = useState('catalogue')

  const [loading, setLoading] = useState(true)
  const [available, setAvailable] = useState([])
  const [domains, setDomains] = useState([])
  const [fileTypes, setFileTypes] = useState([])
  const [addingSlug, setAddingSlug] = useState(null)

  const [search, setSearch] = useState('')
  const [domainFilter, setDomainFilter] = useState(defaultDomainId || 'all')

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [taskDomainId, setTaskDomainId] = useState(defaultDomainId || '')
  const [obligationKind, setObligationKind] = useState('task')
  const [fileTypeId, setFileTypeId] = useState('')
  const [cadenceDays, setCadenceDays] = useState(365)
  const [criticality, setCriticality] = useState('medium')
  const [regulatoryFramework, setRegulatoryFramework] = useState('')
  const [citationUrl, setCitationUrl] = useState('')
  const [penaltySeverity, setPenaltySeverity] = useState('none')
  const [cadenceBasis, setCadenceBasis] = useState('risk_assessment')
  const [savingCustom, setSavingCustom] = useState(false)

  const [showAdvanced, setShowAdvanced] = useState(false)
  const [appliesResidential, setAppliesResidential] = useState(true)
  const [appliesCommercial, setAppliesCommercial] = useState(true)
  const [appliesMixedUse, setAppliesMixedUse] = useState(true)
  const [minHeight, setMinHeight] = useState('')
  const [maxHeight, setMaxHeight] = useState('')
  const [minStoreys, setMinStoreys] = useState('')
  const [maxStoreys, setMaxStoreys] = useState('')
  const [minUnits, setMinUnits] = useState('')
  const [maxUnits, setMaxUnits] = useState('')
  const [minResidentialUnits, setMinResidentialUnits] = useState('')

  const [showNewDomain, setShowNewDomain] = useState(false)
  const [newDomainName, setNewDomainName] = useState('')
  const [newDomainColour, setNewDomainColour] = useState('')
  const [creatingDomain, setCreatingDomain] = useState(false)

  useEffect(() => {
    let alive = true
    setLoading(true)
    Promise.all([fetchAvailableObligations(bpId), fetchTaskDomains(), getFileTypes()])
      .then(([availRes, domRes, ftRes]) => {
        if (!alive) return
        setAvailable(propOr([], 'data', availRes.data))
        setDomains(domRes?.data?.data ?? domRes?.data ?? [])
        setFileTypes(ftRes?.data?.data ?? ftRes?.data ?? [])
      })
      .catch(() => { if (alive) toast.error("Couldn't load obligations to add.") })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [bpId])

  const domainOptions = useMemo(() => {
    const seen = new Map()
    available.forEach(o => {
      if (o.taskDomainId && !seen.has(o.taskDomainId)) {
        seen.set(o.taskDomainId, o.taskDomainName || 'Other')
      }
    })
    return [...seen.entries()].map(([id, label]) => ({ id, label }))
  }, [available])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return available.filter(o => {
      if (domainFilter !== 'all' && o.taskDomainId !== domainFilter) return false
      if (!q) return true
      const hay = `${o.name || ''} ${o.regulatoryFramework || ''} ${o.description || ''}`.toLowerCase()
      return hay.includes(q)
    })
  }, [available, search, domainFilter])

  const recommended = filtered.filter(o => o.applies)
  const others = filtered.filter(o => !o.applies)

  const addFromCatalogue = obligation => {
    if (addingSlug) return
    setAddingSlug(obligation.librarySlug)
    addProgrammeObligation(bpId, { librarySlug: obligation.librarySlug })
      .then(() => {
        toast.success(`"${obligation.name}" added to this building.`)
        setAvailable(prev => prev.filter(o => o.librarySlug !== obligation.librarySlug))
        onAdded?.()
      })
      .catch(e => toast.error(e?.response?.data?.message || 'Could not add that obligation.'))
      .finally(() => setAddingSlug(null))
  }

  const canSubmitCustom = name.trim().length > 0 && taskDomainId

  const submitCustom = () => {
    if (!canSubmitCustom || savingCustom) return
    setSavingCustom(true)
    addProgrammeObligation(bpId, {
      name: name.trim(),
      description: description.trim() || null,
      taskDomainId,
      obligationKind,
      fileTypeId: obligationKind === 'file_type' ? (fileTypeId || null) : null,
      cadenceDays: cadenceDays === '' ? null : cadenceDays,
      criticality,
      penaltySeverity,
      cadenceBasis,
      regulatoryFramework: regulatoryFramework.trim() || null,
      citationUrl: citationUrl.trim() || null,
      appliesToResidential: appliesResidential,
      appliesToCommercial: appliesCommercial,
      appliesToMixedUse: appliesMixedUse,
      minHeightMetres: numOrNull(minHeight),
      maxHeightMetres: numOrNull(maxHeight),
      minStoreys: numOrNull(minStoreys),
      maxStoreys: numOrNull(maxStoreys),
      minUnitCount: numOrNull(minUnits),
      maxUnitCount: numOrNull(maxUnits),
      minResidentialUnits: numOrNull(minResidentialUnits),
    })
      .then(() => {
        toast.success(`"${name.trim()}" added to this building.`)
        onAdded?.()
        onClose?.()
      })
      .catch(e => toast.error(e?.response?.data?.message || 'Could not add that obligation.'))
      .finally(() => setSavingCustom(false))
  }

  const createDomain = () => {
    const nm = newDomainName.trim()
    if (!nm || creatingDomain) return
    setCreatingDomain(true)
    createTaskDomain({ name: nm, colour: newDomainColour || null })
      .then(res => {
        const created = res?.data?.data ?? res?.data
        if (!created?.id) {
          throw new Error('empty response')
        }
        setDomains(prev => [...prev, created])
        setTaskDomainId(created.id)
        setShowNewDomain(false)
        setNewDomainName('')
        setNewDomainColour('')
        toast.success(`Domain "${created.name}" created.`)
      })
      .catch(e => toast.error(e?.response?.data?.message || 'Could not create the domain.'))
      .finally(() => setCreatingDomain(false))
  }

  const defaultDomainName = useMemo(
    () => domains.find(d => d.id === defaultDomainId)?.name || null,
    [domains, defaultDomainId]
  )

  return createPortal(
    <Backdrop onClick={onClose}>
      <Panel onClick={e => e.stopPropagation()}>
        <Header>
          <HeaderText>
            <Eyebrow><AddCircleOutlineRoundedIcon style={{ fontSize: 14 }} /> Add obligation</Eyebrow>
            <Title>
              Add to this building{defaultDomainName ? ` · ${defaultDomainName}` : ''}
            </Title>
            <Sub>Add something the automatic review missed. Pick a standard obligation, or define your own.</Sub>
          </HeaderText>
          <CloseBtn onClick={onClose} aria-label='Close'><CloseRoundedIcon /></CloseBtn>
        </Header>

        <Tabs>
          <TabBtn $active={tab === 'catalogue'} onClick={() => setTab('catalogue')}>From catalogue</TabBtn>
          <TabBtn $active={tab === 'custom'} onClick={() => setTab('custom')}>Custom</TabBtn>
        </Tabs>

        {tab === 'catalogue' ? (
          <Body>
            <Controls>
              <SearchWrap>
                <SearchRoundedIcon style={{ fontSize: 18, color: '#94a3b8' }} />
                <SearchInput
                  placeholder='Search the UK obligation library…'
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </SearchWrap>
              <SearchableSelect
                value={domainFilter}
                onChange={v => setDomainFilter(v)}
                options={[{ value: 'all', label: 'All domains' }, ...domainOptions.map(d => ({ value: d.id, label: d.label }))]}
                ariaLabel='Filter by domain'
              />
            </Controls>

            {loading ? (
              <SkelList>
                {[0, 1, 2, 3, 4].map(i => (
                  <SkelRow key={i}>
                    <SkelLines>
                      <SkelLine style={{ width: '52%' }} />
                      <SkelLine style={{ width: '34%', height: 9 }} />
                    </SkelLines>
                    <SkelPill />
                  </SkelRow>
                ))}
              </SkelList>
            ) : filtered.length === 0 ? (
              <EmptyState>
                {available.length === 0
                  ? 'Every catalogue obligation is already on this building. Use the Custom tab to add a bespoke one.'
                  : 'No catalogue obligations match your search.'}
              </EmptyState>
            ) : (
              <List>
                {recommended.length > 0 && (
                  <GroupLabel>Recommended for this building</GroupLabel>
                )}
                {recommended.map(o => (
                  <ObRow key={o.librarySlug} o={o} adding={addingSlug === o.librarySlug} onAdd={() => addFromCatalogue(o)} />
                ))}
                {others.length > 0 && (
                  <GroupLabel $muted>Other obligations{recommended.length > 0 ? ' (may not apply at this height/use)' : ''}</GroupLabel>
                )}
                {others.map(o => (
                  <ObRow key={o.librarySlug} o={o} adding={addingSlug === o.librarySlug} onAdd={() => addFromCatalogue(o)} />
                ))}
              </List>
            )}
          </Body>
        ) : (
          <Body>
            <Field>
              <FieldLabel htmlFor='cob-name'>Name</FieldLabel>
              <Input id='cob-name' autoFocus value={name} onChange={e => setName(e.target.value)}
                placeholder='e.g. "Fire Risk Appraisal of External Walls"' maxLength={200} />
            </Field>

            <Field>
              <FieldLabel htmlFor='cob-desc'>Description (optional)</FieldLabel>
              <TextArea id='cob-desc' value={description} onChange={e => setDescription(e.target.value)}
                placeholder='What does this obligation require, and why?' rows={3} maxLength={2000} />
            </Field>

            <TwoCol>
              <Field>
                <FieldLabel htmlFor='cob-domain'>Subsection (domain)</FieldLabel>
                {!showNewDomain ? (
                  <>
                    <SearchableSelect
                      id='cob-domain'
                      value={taskDomainId}
                      onChange={v => setTaskDomainId(v)}
                      options={domains.map(d => ({ value: d.id, label: d.name }))}
                      placeholder='Choose a domain…'
                      searchPlaceholder='Search domains…'
                      ariaLabel='Subsection (domain)'
                    />
                    <LinkBtn type='button' onClick={() => setShowNewDomain(true)}>
                      <AddCircleOutlineRoundedIcon style={{ fontSize: 15 }} /> New domain
                    </LinkBtn>
                  </>
                ) : (
                  <NewDomainBox>
                    <Input value={newDomainName} onChange={e => setNewDomainName(e.target.value)}
                      placeholder='New domain name, e.g. "Lift safety"' maxLength={128} autoFocus />
                    <NewDomainRow>
                      <ColourDots>
                        {DOMAIN_COLOURS.map(c => (
                          <ColourDot key={c} type='button' $c={c} $on={newDomainColour === c}
                            onClick={() => setNewDomainColour(c)} aria-label={`Colour ${c}`} />
                        ))}
                      </ColourDots>
                      <NewDomainActions>
                        <MiniSave type='button' disabled={!newDomainName.trim() || creatingDomain} onClick={createDomain}>
                          {creatingDomain ? 'Creating…' : 'Create'}
                        </MiniSave>
                        <MiniCancel type='button' onClick={() => { setShowNewDomain(false); setNewDomainName(''); setNewDomainColour('') }}>
                          Cancel
                        </MiniCancel>
                      </NewDomainActions>
                    </NewDomainRow>
                    <Hint>Add an AI classification prompt later in Task domains.</Hint>
                  </NewDomainBox>
                )}
              </Field>
              <Field>
                <FieldLabel>Met by</FieldLabel>
                <Toggle>
                  {KINDS.map(k => (
                    <ToggleBtn key={k.value} $active={obligationKind === k.value} onClick={() => setObligationKind(k.value)}>
                      {k.label}
                    </ToggleBtn>
                  ))}
                </Toggle>
                <Hint>{obligationKind === 'task' ? 'Satisfied by completing a recurring task.' : 'Satisfied by uploading a matching document.'}</Hint>
              </Field>
            </TwoCol>

            {obligationKind === 'file_type' && (
              <Field>
                <FieldLabel>Which document type satisfies this?</FieldLabel>
                <FileTypePicker
                  options={fileTypes}
                  selectedId={fileTypeId}
                  onChange={setFileTypeId}
                />
                <Hint>Uploading a document of this type will satisfy the obligation. Leave as auto-detect to let Guru match it for you.</Hint>
              </Field>
            )}

            <TwoCol>
              <Field>
                <FieldLabel>Criticality</FieldLabel>
                <CritRow>
                  {CRITICALITY_LEVELS.map(c => (
                    <CritBtn key={c.key} $active={criticality === c.key} $tint={c.colour} onClick={() => setCriticality(c.key)}>
                      <Dot $tint={c.colour} /> {c.label}
                    </CritBtn>
                  ))}
                </CritRow>
              </Field>
              <Field>
                <FieldLabel>Cadence</FieldLabel>
                <Pills>
                  {CADENCE_PRESETS.map(p => (
                    <Pill key={p.label} $active={cadenceDays === p.days} onClick={() => setCadenceDays(p.days)}>
                      {p.label}
                    </Pill>
                  ))}
                </Pills>
              </Field>
            </TwoCol>

            <Field>
              <FieldLabel>Penalty severity</FieldLabel>
              <FieldExplain>How serious are the legal consequences if this duty is breached? This weighs into the compliance score. A breach of a higher-penalty obligation counts against it far more.</FieldExplain>
              <PenaltyList role='radiogroup' aria-label='Penalty severity'>
                {Object.entries(PENALTY_SEVERITIES).map(([key, p]) => (
                  <PenaltyCard key={key} type='button' role='radio' aria-checked={penaltySeverity === key}
                    $active={penaltySeverity === key} $tint={p.colour} onClick={() => setPenaltySeverity(key)}>
                    <PenaltyRadio $active={penaltySeverity === key} $tint={p.colour} />
                    <PenaltyText>
                      <PenaltyName>{p.label}</PenaltyName>
                      {p.desc && <PenaltyDesc>{p.desc}</PenaltyDesc>}
                    </PenaltyText>
                  </PenaltyCard>
                ))}
              </PenaltyList>
              <Hint>Not sure? Routine or contractual duties are usually “No specific penalty”. Statutory safety duties. Fire, gas, electrical, structural. Usually carry a fine or unlimited fine.</Hint>
            </Field>

            <Field>
              <FieldLabel>Cadence basis</FieldLabel>
              <Toggle>
                {CADENCE_BASES.map(b => (
                  <ToggleBtn key={b.value} type='button' $active={cadenceBasis === b.value} onClick={() => setCadenceBasis(b.value)}>
                    {b.label}
                  </ToggleBtn>
                ))}
              </Toggle>
              <Hint>Where the frequency comes from. Set by law (statutory), recommended in guidance, or decided by a risk assessment.</Hint>
            </Field>

            <TwoCol>
              <Field>
                <FieldLabel htmlFor='cob-fw'>Your reference / framework (optional)</FieldLabel>
                <Input id='cob-fw' value={regulatoryFramework} onChange={e => setRegulatoryFramework(e.target.value)}
                  placeholder='e.g. "PAS 9980" or "Lease schedule 4"' maxLength={80} />
              </Field>
              <Field>
                <FieldLabel htmlFor='cob-cite'>Reference link (optional)</FieldLabel>
                <Input id='cob-cite' type='url' value={citationUrl} onChange={e => setCitationUrl(e.target.value)}
                  placeholder='https://…' maxLength={500} />
              </Field>
            </TwoCol>

            <AdvToggle type='button' onClick={() => setShowAdvanced(s => !s)}>
              {showAdvanced ? '▾' : '▸'} Advanced. Where this obligation applies (optional)
            </AdvToggle>
            {showAdvanced && (
              <AdvBox>
                <Field>
                  <FieldLabel>Applies to building types</FieldLabel>
                  <Pills>
                    <Pill type='button' $active={appliesResidential} onClick={() => setAppliesResidential(v => !v)}>Residential</Pill>
                    <Pill type='button' $active={appliesCommercial} onClick={() => setAppliesCommercial(v => !v)}>Commercial</Pill>
                    <Pill type='button' $active={appliesMixedUse} onClick={() => setAppliesMixedUse(v => !v)}>Mixed use</Pill>
                  </Pills>
                  <Hint>Leave all on if it applies to any building type.</Hint>
                </Field>
                <TwoCol>
                  <Field>
                    <FieldLabel>Height (m)</FieldLabel>
                    <RangeRow>
                      <Input type='number' min='0' value={minHeight} onChange={e => setMinHeight(e.target.value)} placeholder='Min' />
                      <Input type='number' min='0' value={maxHeight} onChange={e => setMaxHeight(e.target.value)} placeholder='Max' />
                    </RangeRow>
                  </Field>
                  <Field>
                    <FieldLabel>Storeys</FieldLabel>
                    <RangeRow>
                      <Input type='number' min='0' value={minStoreys} onChange={e => setMinStoreys(e.target.value)} placeholder='Min' />
                      <Input type='number' min='0' value={maxStoreys} onChange={e => setMaxStoreys(e.target.value)} placeholder='Max' />
                    </RangeRow>
                  </Field>
                </TwoCol>
                <TwoCol>
                  <Field>
                    <FieldLabel>Units</FieldLabel>
                    <RangeRow>
                      <Input type='number' min='0' value={minUnits} onChange={e => setMinUnits(e.target.value)} placeholder='Min' />
                      <Input type='number' min='0' value={maxUnits} onChange={e => setMaxUnits(e.target.value)} placeholder='Max' />
                    </RangeRow>
                  </Field>
                  <Field>
                    <FieldLabel>Min. residential units</FieldLabel>
                    <Input type='number' min='0' value={minResidentialUnits} onChange={e => setMinResidentialUnits(e.target.value)} placeholder='Any' />
                  </Field>
                </TwoCol>
                <Hint>These mirror how built-in obligations decide which buildings they apply to. Leave blank for no limit.</Hint>
              </AdvBox>
            )}
          </Body>
        )}

        {tab === 'custom' && (
          <Footer>
            <Cancel onClick={onClose}>Cancel</Cancel>
            <Save onClick={submitCustom} disabled={savingCustom || !canSubmitCustom}>
              {savingCustom ? 'Adding…' : 'Add to building'}
            </Save>
          </Footer>
        )}
      </Panel>
    </Backdrop>,
    document.body
  )
}

const ObRow = ({ o, adding, onAdd }) => (
  <Row>
    <RowMain>
      <RowName>{o.name}</RowName>
      <RowMeta>
        {o.regulatoryFramework && <MetaChip>{o.regulatoryFramework}</MetaChip>}
        {o.taskDomainName && <MetaChip $tint={o.taskDomainColour}>{o.taskDomainName}</MetaChip>}
        <MetaChip>{cadenceText(o.cadenceDays)}</MetaChip>
        {o.obligationKind === 'task' ? <MetaChip>Task</MetaChip> : <MetaChip>Document</MetaChip>}
        {!o.applies && o.minHeightMetres != null && <MetaChip $warn>≥{o.minHeightMetres}m</MetaChip>}
        {o.citationUrl && (
          <CiteLink href={o.citationUrl} target='_blank' rel='noopener noreferrer' onClick={e => e.stopPropagation()}>
            Source <OpenInNewRoundedIcon style={{ fontSize: 11 }} />
          </CiteLink>
        )}
      </RowMeta>
      {o.description && <RowDesc>{o.description}</RowDesc>}
    </RowMain>
    <AddBtn onClick={onAdd} disabled={adding}>
      {adding ? '…' : <><AddCircleOutlineRoundedIcon style={{ fontSize: 15 }} /> Add</>}
    </AddBtn>
  </Row>
)

const FileTypePicker = ({ options, selectedId, onChange }) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const selected = options.find(o => o.id === selectedId)
  const display = selected?.name || 'Auto-detect'
  const q = query.trim().toLowerCase()
  const filtered = q ? options.filter(o => (o.name || '').toLowerCase().includes(q)) : options
  return (
    <FtWrap>
      <FtBtn type='button' onClick={() => setOpen(o => !o)} $empty={!selectedId}>
        {display}
      </FtBtn>
      {open && (
        <FtPanel onMouseLeave={() => { setOpen(false); setQuery('') }}>
          <FtSearch
            autoFocus
            placeholder='Search document types…'
            value={query}
            onChange={e => setQuery(e.target.value)}
            onClick={e => e.stopPropagation()}
          />
          <FtOption $active={!selectedId} onClick={() => { onChange(''); setOpen(false); setQuery('') }}>
            Auto-detect
          </FtOption>
          {filtered.length === 0
            ? <FtEmpty>{options.length === 0 ? 'No document types available.' : 'No matches.'}</FtEmpty>
            : filtered.map(o => (
              <FtOption key={o.id} $active={selectedId === o.id}
                onClick={() => { onChange(o.id); setOpen(false); setQuery('') }}>
                {o.name}
              </FtOption>
            ))}
        </FtPanel>
      )}
    </FtWrap>
  )
}

export default AddProgrammeObligationModal

const pulse = keyframes`0%,100%{opacity:0.55}50%{opacity:0.85}`

const FtWrap = styled.div` position: relative; `

const FtBtn = styled.button`
  font: inherit; font-size: 13px; text-align: left;
  width: 100%; box-sizing: border-box;
  border: 1px solid #ececec; border-radius: 10px; padding: 10px 12px; background: #fff;
  color: ${({ $empty, theme }) => $empty ? '#8a8a93' : theme.colors.haiti};
  cursor: pointer;
  &:focus { outline: 2px solid #33becc; outline-offset: -1px; }
`

const FtPanel = styled.div`
  position: absolute; z-index: 20; top: calc(100% + 4px); left: 0; right: 0;
  max-height: 260px; overflow-y: auto;
  background: #fff; border: 1px solid #ececec; border-radius: 10px;
  box-shadow: 0 8px 24px rgba(12, 18, 41, 0.12);
`

const FtSearch = styled.input`
  position: sticky; top: 0;
  width: 100%; box-sizing: border-box;
  font: inherit; font-size: 12.5px;
  border: none; border-bottom: 1px solid #ececec;
  padding: 8px 10px; background: #fff;
  color: ${({ theme }) => theme.colors.haiti};
  &:focus { outline: none; }
`

const FtOption = styled.div`
  font-size: 12.5px; padding: 8px 12px; cursor: pointer;
  color: ${({ theme }) => theme.colors.haiti};
  background: ${({ $active }) => $active ? '#eefbfc' : '#fff'};
  font-weight: ${({ $active }) => $active ? 700 : 500};
  &:hover { background: #f4f5f7; }
`

const FtEmpty = styled.div` font-size: 12px; color: #8a8a93; padding: 10px 12px; `

const Backdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(12, 19, 43, 0.45);
  z-index: 1300;
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
`

const Panel = styled.div`
  background: #fff;
  border-radius: 16px;
  width: 100%; max-width: 660px;
  max-height: calc(100vh - 48px);
  overflow: hidden;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 48px rgba(0,0,0,0.18);
`

const Header = styled.div`
  display: flex; justify-content: space-between; align-items: flex-start;
  padding: 20px 24px 14px;
`

const HeaderText = styled.div` display: flex; flex-direction: column; gap: 4px; `

const Eyebrow = styled.span`
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 10.5px; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase;
  color: #1f8c97;
`

const Title = styled.h2`
  margin: 0; font-size: 17px; font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const Sub = styled.p`
  margin: 2px 0 0; font-size: 12.5px; line-height: 1.45; color: #6e6e78;
`

const CloseBtn = styled.button`
  background: none; border: none; cursor: pointer;
  color: ${({ theme }) => theme.colors.grey[800]};
  padding: 4px; border-radius: 6px; display: inline-flex;
  &:hover { background: #f4f4f6; }
`

const Tabs = styled.div`
  display: flex; gap: 4px;
  padding: 0 24px;
  border-bottom: 1px solid #f0f0f2;
`

const TabBtn = styled.button`
  font: inherit;
  background: none; border: none; cursor: pointer;
  padding: 10px 4px; margin-right: 18px;
  font-size: 13px; font-weight: 700;
  color: ${({ $active, theme }) => $active ? theme.colors.haiti : '#8a8a93'};
  border-bottom: 2px solid ${({ $active, theme }) => $active ? theme.colors.secondary : 'transparent'};
  margin-bottom: -1px;
`

const Body = styled.div`
  padding: 18px 24px;
  overflow-y: auto;
  display: flex; flex-direction: column; gap: 16px;
`

const Controls = styled.div` display: flex; gap: 10px; flex-wrap: wrap; `

const SearchWrap = styled.div`
  display: flex; align-items: center; gap: 8px;
  flex: 1; min-width: 220px;
  padding: 8px 12px;
  border: 1px solid #ececec; border-radius: 10px;
`

const SearchInput = styled.input`
  font: inherit; flex: 1; min-width: 0;
  border: none; outline: none; background: transparent;
  font-size: 13.5px; color: ${({ theme }) => theme.colors.haiti};
`

const List = styled.div` display: flex; flex-direction: column; gap: 8px; `

const GroupLabel = styled.div`
  font-size: 10.5px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase;
  color: ${({ $muted }) => $muted ? '#a0a0a8' : '#1f8c97'};
  margin: 6px 0 2px;
`

const Row = styled.div`
  display: flex; align-items: flex-start; gap: 12px;
  padding: 12px 14px;
  border: 1px solid #ececec; border-radius: 12px;
  &:hover { border-color: #d8d8dc; background: #fafbfc; }
`

const RowMain = styled.div` flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px; `

const RowName = styled.div`
  font-size: 13.5px; font-weight: 700; line-height: 1.35;
  color: ${({ theme }) => theme.colors.haiti};
`

const RowMeta = styled.div` display: flex; flex-wrap: wrap; align-items: center; gap: 6px; `

const MetaChip = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 10.5px; font-weight: 700;
  padding: 2px 8px; border-radius: 999px;
  color: ${({ $tint, $warn }) => $warn ? '#c2410c' : ($tint || '#5a5a63')};
  background: ${({ $tint, $warn }) => $warn ? '#ffedd5' : ($tint ? `${$tint}14` : '#f1f1f4')};
  border: 1px solid ${({ $tint, $warn }) => $warn ? '#fed7aa' : ($tint ? `${$tint}33` : '#e6e6ea')};
`

const CiteLink = styled.a`
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 10.5px; font-weight: 700; color: #1f8c97; text-decoration: none;
  &:hover { text-decoration: underline; }
`

const RowDesc = styled.div`
  font-size: 12px; line-height: 1.45; color: #6e6e78;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
`

const AddBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.secondary};
  color: #fff; border: none;
  padding: 7px 14px; border-radius: 999px;
  font-size: 12px; font-weight: 700; cursor: pointer;
  &:hover:not(:disabled) { filter: brightness(1.06); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`

const EmptyState = styled.div`
  padding: 28px 20px; text-align: center;
  font-size: 13px; line-height: 1.5; color: #6e6e78;
  background: #fafbfc; border: 1px dashed #e4e4e7; border-radius: 12px;
`

const SkelList = styled.div` display: flex; flex-direction: column; gap: 8px; `

const SkelRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 14px; border: 1px solid #f0f0f2; border-radius: 12px;
`

const SkelLines = styled.div` flex: 1; display: flex; flex-direction: column; gap: 8px; `

const SkelLine = styled.div`
  height: 11px; border-radius: 6px; background: #ececef;
  animation: ${pulse} 1.4s ease-in-out infinite;
`

const SkelPill = styled.div`
  width: 64px; height: 28px; border-radius: 999px; background: #ececef;
  animation: ${pulse} 1.4s ease-in-out infinite;
`

const Field = styled.div` display: flex; flex-direction: column; gap: 8px; `

const TwoCol = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
  @media (max-width: 540px) { grid-template-columns: 1fr; }
`

const FieldLabel = styled.label`
  font-size: 11px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Hint = styled.span` font-size: 11.5px; color: #8a8a93; `

const Input = styled.input`
  font: inherit; font-size: 13px;
  border: 1px solid #ececec; border-radius: 10px; padding: 10px 12px;
  color: ${({ theme }) => theme.colors.haiti};
  &:focus { outline: 2px solid #33becc; outline-offset: -1px; }
`

const TextArea = styled.textarea`
  font: inherit; font-size: 13px;
  border: 1px solid #ececec; border-radius: 10px; padding: 10px 12px;
  resize: vertical; min-height: 64px;
  color: ${({ theme }) => theme.colors.haiti};
  &:focus { outline: 2px solid #33becc; outline-offset: -1px; }
`

const Toggle = styled.div`
  display: inline-flex; background: ${({ theme }) => theme.colors.lighterGrey};
  border-radius: 999px; padding: 3px; width: fit-content;
`

const ToggleBtn = styled.button`
  font: inherit;
  background: ${({ $active }) => $active ? '#fff' : 'transparent'};
  color: ${({ theme }) => theme.colors.haiti};
  border: none; font-size: 12px; font-weight: 700;
  padding: 6px 14px; border-radius: 999px; cursor: pointer;
  box-shadow: ${({ $active }) => $active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none'};
`

const CritRow = styled.div` display: flex; flex-wrap: wrap; gap: 6px; `

const CritBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 700;
  padding: 6px 12px; border-radius: 999px; cursor: pointer;
  color: ${({ $active, $tint }) => $active ? $tint : '#6e6e78'};
  background: ${({ $active, $tint }) => $active ? `${$tint}14` : '#fff'};
  border: 1px solid ${({ $active, $tint }) => $active ? `${$tint}59` : '#ececec'};
`

const Dot = styled.span`
  width: 8px; height: 8px; border-radius: 50%;
  background: ${({ $tint }) => $tint};
`

const Pills = styled.div` display: flex; flex-wrap: wrap; gap: 6px; `

const Pill = styled.button`
  font: inherit;
  background: ${({ $active, theme }) => $active ? '#fff' : theme.colors.lighterGrey};
  border: 1px solid ${({ $active }) => $active ? '#0c1229' : '#ececec'};
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 12px; font-weight: 700;
  padding: 6px 12px; border-radius: 999px; cursor: pointer;
`

const Footer = styled.div`
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 14px 24px; border-top: 1px solid #f4f4f6;
`

const Cancel = styled.button`
  font: inherit;
  background: none; border: 1px solid #ececec; border-radius: 8px;
  padding: 8px 16px; font-size: 13px; font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti}; cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const Save = styled.button`
  font: inherit;
  background: ${({ theme }) => theme.colors.secondary};
  color: #fff; border: none; border-radius: 8px;
  padding: 8px 18px; font-size: 13px; font-weight: 700; cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { filter: brightness(1.06); }
`

const LinkBtn = styled.button`
  font: inherit;
  align-self: flex-start;
  display: inline-flex; align-items: center; gap: 4px;
  background: none; border: none; cursor: pointer;
  padding: 4px 0;
  font-size: 12px; font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};
  &:hover { text-decoration: underline; }
`

const NewDomainBox = styled.div`
  display: flex; flex-direction: column; gap: 8px;
  padding: 10px; border: 1px solid #ececec; border-radius: 8px;
  background: #fafbfc;
`

const NewDomainRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap;
`

const ColourDots = styled.div` display: flex; gap: 6px; flex-wrap: wrap; `

const ColourDot = styled.button`
  width: 20px; height: 20px; border-radius: 50%; cursor: pointer;
  background: ${({ $c }) => $c};
  border: 2px solid ${({ $on }) => ($on ? '#0c1229' : 'transparent')};
  box-shadow: 0 0 0 1px #e4e4e7;
`

const NewDomainActions = styled.div` display: inline-flex; align-items: center; gap: 8px; `

const MiniSave = styled.button`
  font: inherit;
  background: ${({ theme }) => theme.colors.secondary};
  color: #fff; border: none; border-radius: 7px;
  padding: 6px 14px; font-size: 12px; font-weight: 700; cursor: pointer;
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`

const MiniCancel = styled.button`
  font: inherit;
  background: none; border: none; cursor: pointer;
  font-size: 12px; font-weight: 700; color: #8a8a93;
  &:hover { color: ${({ theme }) => theme.colors.haiti}; }
`

const AdvToggle = styled.button`
  font: inherit;
  align-self: flex-start;
  background: none; border: none; cursor: pointer;
  padding: 2px 0;
  font-size: 12.5px; font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  &:hover { color: ${({ theme }) => theme.colors.secondary}; }
`

const AdvBox = styled.div`
  display: flex; flex-direction: column; gap: 14px;
  padding: 14px; border: 1px solid #ececec; border-radius: 10px;
  background: #fafbfc;
`

const RangeRow = styled.div`
  display: flex; gap: 8px;
  & > * { flex: 1; min-width: 0; }
`

const FieldExplain = styled.p`
  margin: 0;
  font-size: 12px; line-height: 1.45;
  color: #6e6e78;
`

const PenaltyList = styled.div` display: flex; flex-direction: column; gap: 6px; `

const PenaltyCard = styled.button`
  font: inherit;
  display: flex; align-items: flex-start; gap: 10px;
  width: 100%; text-align: left;
  padding: 10px 12px;
  border-radius: 9px; cursor: pointer;
  border: 1px solid ${({ $active, $tint }) => ($active ? $tint : '#ececec')};
  background: ${({ $active, $tint }) => ($active ? `${$tint}0f` : '#fff')};
  transition: border-color 120ms ease, background 120ms ease;
  &:hover { border-color: ${({ $tint }) => $tint}; }
`

const PenaltyRadio = styled.span`
  flex-shrink: 0; margin-top: 1px;
  width: 16px; height: 16px; border-radius: 50%;
  border: 2px solid ${({ $active, $tint }) => ($active ? $tint : '#c9c9d0')};
  background: ${({ $active, $tint }) => ($active ? $tint : '#fff')};
  box-shadow: ${({ $active }) => ($active ? 'inset 0 0 0 2px #fff' : 'none')};
`

const PenaltyText = styled.span` display: flex; flex-direction: column; gap: 2px; min-width: 0; `

const PenaltyName = styled.span`
  font-size: 13px; font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const PenaltyDesc = styled.span`
  font-size: 12px; line-height: 1.4;
  color: #6e6e78;
`
